import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Chip, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { User } from '../../types';
import { fetchAllUsers, updateUser } from '../../services/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const UserManagementPanel: React.FC = () => {
    const queryClient = useQueryClient();
    const { data: users = [], isLoading, error } = useQuery({
        queryKey: ['users'],
        queryFn: fetchAllUsers,
    });

    const updateUserMutation = useMutation({
        mutationFn: (data: { userId: number; payload: { is_admin: boolean } }) =>
            updateUser(data.userId, data.payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });

    const handleAdminToggle = (id: number, currentStatus: boolean) => {
        updateUserMutation.mutate({ userId: id, payload: { is_admin: !currentStatus } });
    };

    const columns: GridColDef<User>[] = [
        { field: 'id', headerName: 'ID', width: 90 },
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'email', headerName: 'Email', flex: 1 },
        {
            field: 'is_admin',
            headerName: 'Admin',
            width: 120,
            renderCell: (params) => (
                <Chip
                    icon={params.value ? <CheckCircleIcon /> : <CancelIcon />}
                    label={params.value ? 'Yes' : 'No'}
                    color={params.value ? 'success' : 'error'}
                    onClick={() => handleAdminToggle(params.row.id, params.value)}
                    size="small"
                    sx={{ cursor: 'pointer' }}
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Registered On',
            type: 'dateTime',
            width: 200,
            valueGetter: (params: GridValueGetterParams<User>) =>
                params.row.created_at ? new Date(params.row.created_at) : null,
        },
    ];

    if (error) {
        return <Alert severity="error">{(error as Error).message}</Alert>;
    }

    return (
        <Paper sx={{ p: 2, height: '70vh', mt: 4, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                User Management
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
                <DataGrid
                    rows={users}
                    columns={columns}
                    loading={isLoading}
                    sx={{ border: 0 }}
                    getRowId={(row) => row.id}
                />
            </Box>
        </Paper>
    );
};

export default UserManagementPanel;
